import { useEffect, useState } from 'react'
import { markAttendanceWorkOpened } from '../dialogStandDown'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, CalendarClock, Check } from 'lucide-react'
import {
  byNight,
  byTeacher,
  clockTime,
  defaultSession,
  fetchRegisterClasses,
  fetchRegisterSessions,
  fetchRegisters,
  fetchSheet,
  markAttendance,
  meridiem,
  type AttendanceState,
  type RegisterRow,
} from '../api/attendance'
import { formatFullDate } from '../api/calendar'
import { readable } from './AttendancePanel'
import AddToNight from './AddToNight'
import { MarkButtons } from './MarkButtons'

/**
 * Registers that still need filling in — the other half of D-118.
 *
 * Unmarked sessions are excluded from a dancer's percentage rather than counted
 * as absences, which is only honest if the gap can be *found*. Otherwise
 * "3 sessions not yet marked" is a number with nowhere to go.
 *
 * Anything filled in here is flagged **backdated**, because a register written
 * a week later is a different kind of evidence from one taken in the room, and
 * a habit of it is worth being able to see.
 */
export default function MissingAttendanceView() {
  const [incompleteOnly, setIncompleteOnly] = useState(true)
  const [byWhom, setByWhom] = useState(false)
  const [open, setOpen] = useState<RegisterRow | null>(null)

  /*
   * <b>By teacher, the window comes whole</b> (D-743). *Who is not taking
   * attendance* cannot be answered from the gaps alone - a teacher who has
   * taken forty registers and missed one would read the same as one who has
   * taken none - so this view asks for the marked ones too, whatever the
   * checkbox says. It is a second cache entry rather than a second endpoint,
   * and the first is still there when she switches back.
   */
  const whole = byWhom || !incompleteOnly

  const { data, isPending, isError } = useQuery({
    queryKey: ['registers', whole],
    queryFn: () => fetchRegisters(!whole),
  })

  if (open) return <BackdatedSheet row={open} onClose={() => setOpen(null)} />

  const nights = byNight(data ?? [])
  const outstanding = (data ?? []).filter((r) => r.isIncomplete).length

  return (
    <div className="page-shell px-5 py-8">
      <header className="mb-5">
        <p className="text-[11px] font-bold tracking-[0.14em] text-accent uppercase">Attendance</p>
        <h1 className="font-display text-2xl">Missing attendance</h1>
        <p className="mt-1.5 max-w-[42rem] text-[13.5px] text-ink-3">
          The last four weeks. A class nobody marked does not count against anyone &mdash;
          it just leaves a hole in the numbers, so this is where it gets closed.
        </p>
      </header>

      {/*
        <b>The same registers, read two ways</b> (D-743). By night answers
        *what did we miss*; by teacher answers *who is not taking them*, which
        is the owner's question and had no screen at all.
      */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        {/* No `role="group"` on the wrapper: a `details` carries that role
            implicitly, and adding one here put a third "group" on a screen
            whose test counts the folded days. The buttons say what they are
            with `aria-pressed`. */}
        <div className="flex rounded-xl border border-line bg-surface-2 p-0.5">
          {([['night', 'By night'], ['teacher', 'By teacher']] as const).map(([key, label]) => {
            const on = (key === 'teacher') === byWhom
            return (
              <button
                key={key}
                type="button"
                aria-pressed={on}
                onClick={() => setByWhom(key === 'teacher')}
                className={`min-h-[32px] rounded-[10px] px-3 text-[13px] font-semibold ${
                  on ? 'bg-surface text-ink shadow-sm' : 'text-ink-3 hover:text-ink-2'
                }`}
              >
                {label}
              </button>
            )
          })}
        </div>

        {/*
          py-1 for the height: measured 20px, and a touch target wants 24.

          <b>Gone under By teacher</b>, because that view is a comparison and
          needs the taken ones to make it - a control that cannot change what is
          shown is worse than no control.
        */}
        {!byWhom && (
          <label className="flex cursor-pointer items-center gap-2 py-1 text-[13.5px]">
            <input
              type="checkbox"
              checked={incompleteOnly}
              onChange={(e) => setIncompleteOnly(e.target.checked)}
              className="size-4 accent-[var(--color-accent)]"
            />
            Only ones still to finish
          </label>
        )}
      </div>

      {isPending && <p className="text-sm text-ink-3">Loading...</p>}
      {isError && <p role="alert" className="text-sm text-crit">Could not load attendance.</p>}

      {/*
        An empty list has two completely different meanings, and saying the
        wrong one is how attendance came to look broken (D-189). Before the
        season starts nothing has happened yet — "every register is complete"
        would be technically true and read as a system that had lost the data.
      */}
      {data && data.length === 0 && <NothingToShow incompleteOnly={!whole} />}

      {outstanding > 0 && (
        <p className="mb-4 rounded-xl border border-warn bg-warn-bg px-4 py-2.5 text-[13.5px]">
          <strong>{outstanding}</strong> {outstanding === 1 ? 'class' : 'classes'} still to mark.
        </p>
      )}

      {byWhom && <ByTeacher rows={data ?? []} onOpen={setOpen} />}

      <div className="flex flex-col gap-3" hidden={byWhom}>
        {/*
          Each day folds, and only the first starts open (D-590). `asked`:
          *"each day should be collapsible with only the first one open by
          default so you don't have to scroll so far on that page"* - four
          weeks of unmarked registers was a wall.
        */}
        {nights.map((night, i) => (
          <details key={night.date} open={i === 0} className="group">
            {/* `py-1.5` for the height: measured 350x18, and this is the
                control the fold exists for (D-255). */}
            <summary className="mb-1 flex cursor-pointer list-none items-center gap-2 py-1.5 text-[12px] font-bold tracking-[0.11em] text-ink-3 uppercase">
              <span className="inline-block transition-transform group-open:rotate-90" aria-hidden="true">&#9656;</span>
              {formatFullDate(night.date)}
              <span className="tabular font-semibold normal-case tracking-normal text-ink-3">
                &middot; {night.rows.length}
              </span>
            </summary>

            <div className="flex flex-col gap-2">
              {night.rows.map((row) => (
                <RegisterButton key={row.occurrenceId} row={row} onOpen={setOpen} />
              ))}
            </div>
          </details>
        ))}
      </div>

      <OpenAnyRegister onOpen={setOpen} />
    </div>
  )
}

/**
 * Why the list is empty, truthfully (D-189, corrected by D-782).
 *
 * <b>Three situations, and for a year this told two of them apart wrongly.</b>
 * The comment here used to say <i>"if she teaches classes but none has met
 * yet"</i> — and the code only ever asked whether she <b>had</b> classes. The
 * second half was never written. So a teacher who had marked everything was
 * told the season had not started, and a teacher with no classes at all was
 * congratulated on having marked them. Both branches were wrong and they were
 * the wrong way round.
 *
 * It was live in production on 22 September, a fortnight into a season that
 * opened on the 8th, and the owner caught it from a teacher's phone.
 *
 * <b>`met` is what makes the distinction possible</b>: `sessions` counts every
 * dated session a class has, past and future, so it cannot separate <i>nothing
 * has run</i> from <i>everything is marked</i>. The endpoint now sends both.
 *
 * <b>And the wording claims less than it used to.</b> "The season has not
 * started" is a statement about the season, which this screen cannot see —
 * occurrences do not key off season dates (D-42), and on opening night it would
 * be false by teatime. "No classes have met yet" is what is actually known.
 */
function NothingToShow({ incompleteOnly }: { incompleteOnly: boolean }) {
  const { data: classes } = useQuery({
    queryKey: ['register-classes'],
    queryFn: fetchRegisterClasses,
  })

  // Undefined while it loads: say nothing rather than guess a reason, which is
  // D-209 on an empty state - a wrong reason is worse than no reason, because
  // somebody acts on it.
  if (!classes) return null

  const hasClasses = classes.length > 0
  const anythingMet = classes.some((c) => c.met > 0)

  return (
    <div className="rounded-2xl border border-line bg-surface px-5 py-8 text-center">
      {!hasClasses ? (
        <>
          <CalendarClock size={22} strokeWidth={2} className="mx-auto mb-2 text-ink-3" />
          <p className="text-[14.5px] font-semibold">You have no classes here</p>
          <p className="mx-auto mt-1 max-w-[30rem] text-[13px] text-ink-3">
            Nothing is missing because there is nothing to mark. If that is a surprise, the studio
            assigns classes to teachers.
          </p>
        </>
      ) : !anythingMet ? (
        <>
          <CalendarClock size={22} strokeWidth={2} className="mx-auto mb-2 text-ink-3" />
          <p className="text-[14.5px] font-semibold">No classes have met yet</p>
          <p className="mx-auto mt-1 max-w-[30rem] text-[13px] text-ink-3">
            Nothing here is missing &mdash; none of your classes has had a session yet. You can
            still open any class below to look at its attendance or try it out.
          </p>
        </>
      ) : (
        <>
          <Check size={20} strokeWidth={2.5} className="mx-auto mb-2 text-good" />
          <p className="text-[14.5px] font-semibold">
            {incompleteOnly ? 'Every class is marked' : 'No classes ran in this period'}
          </p>
          <p className="mx-auto mt-1 max-w-[30rem] text-[13px] text-ink-3">
            {incompleteOnly
              ? 'Nothing is waiting on you.'
              : 'Nothing met in the last four weeks.'}
          </p>
        </>
      )}
    </div>
  )
}

/**
 * Opening any class's register, on any date it meets (D-189).
 *
 * <b>A different question from the list above.</b> That one is a to-do — past
 * sessions still to be filled in — and before the season starts it is correctly
 * empty, which is exactly when somebody needs to reach a register most.
 *
 * Lands on the most recent session that has happened, else the first upcoming
 * one, which is what "default to right now" means on a calendar that has not
 * started.
 */
function OpenAnyRegister({ onOpen }: { onOpen: (row: RegisterRow) => void }) {
  const [classId, setClassId] = useState('')

  const { data: classes } = useQuery({
    queryKey: ['register-classes'],
    queryFn: fetchRegisterClasses,
  })

  const { data: sessions } = useQuery({
    queryKey: ['register-sessions', classId],
    queryFn: () => fetchRegisterSessions(classId),
    enabled: Boolean(classId),
  })

  if (!classes || classes.length === 0) return null

  const landing = sessions ? defaultSession(sessions) : null

  return (
    <details className="mt-6 rounded-2xl border border-line bg-surface px-4 py-3">
      {/*
        **It said *Open a different class*, and there was often nothing to be
        different from** (D-396).

        `asked`: whether this belongs on Tonya's screen at all. It does — it is
        the only way to reach a class whose attendance is complete, and out of
        season the list above it is correctly empty, which is exactly when
        somebody needs it most (D-189). What was wrong was the word *different*,
        which names this by contrast with a list that is frequently not there.

        Kept collapsed, so on the ordinary day — a list of sessions to finish —
        it stays out of the way of the thing she came for.

        Likewise 20px before the padding: a whole row that a thumb misses.
      */}
      <summary className="cursor-pointer py-1 text-[13.5px] font-semibold">
        Open any class
      </summary>

      <p className="mt-1.5 text-[12.5px] text-ink-3">
        Any class, any week it meets &mdash; including weeks still to come.
      </p>

      <select
        value={classId}
        onChange={(e) => setClassId(e.target.value)}
        className="mt-3 w-full rounded-lg border border-line-2 bg-surface-2 px-3 py-2 text-[13.5px] outline-none focus:border-accent"
      >
        <option value="">Pick a class</option>
        {classes.map((c) => (
          <option key={c.classId} value={c.classId}>
            {c.name}
          </option>
        ))}
      </select>

      {sessions && sessions.length > 0 && (
        <div className="mt-3 flex max-h-[18rem] flex-col gap-1.5 overflow-y-auto">
          {sessions.map((session) => (
            <button
              key={session.occurrenceId}
              type="button"
              onClick={() =>
                onOpen({
                  occurrenceId: session.occurrenceId,
                  className: session.className,
                  date: session.date,
                  startTime: session.startTime,
                  roomName: session.roomName,
                  expected: session.expected,
                  marked: session.marked,
                  isIncomplete: session.isIncomplete,
                  // *Open any class* picks a session from a different endpoint,
                  // which does not carry the teacher - so the sheet says nothing
                  // rather than guessing at one (D-743).
                  teachers: [],
                })
              }
              className={`flex items-center gap-3 rounded-xl border px-3 py-2 text-left text-[13px] ${
                session.occurrenceId === landing?.occurrenceId
                  ? 'border-accent bg-accent-soft'
                  : 'border-line-2 hover:border-ink-3'
              }`}
            >
              <span className="min-w-0 flex-1">
                {formatFullDate(session.date)}
                {session.isCancelled && (
                  <span className="ml-2 text-[11.5px] text-ink-3">cancelled</span>
                )}
              </span>

              {/*
                Said on the row rather than only after opening it. A mark
                against a session that has not happened is stored and does not
                count toward anyone's 80% until the date arrives — which is a
                promise worth making before the tap, not after.
              */}
              {session.isFuture && (
                <span className="shrink-0 rounded-md bg-surface-2 px-1.5 py-0.5 text-[11px] font-semibold text-ink-3">
                  Not yet
                </span>
              )}

              <span className="shrink-0 text-[12px] text-ink-3 tabular">
                {session.marked} of {session.expected}
              </span>
            </button>
          ))}
        </div>
      )}
    </details>
  )
}

/**
 * Filling in a register after the fact.
 *
 * The same roster the tablet shows, with every mark flagged as backdated —
 * a register written a week later is not the same evidence as one taken in the
 * room, and the flag is what makes a pattern of it visible rather than silent.
 */
function BackdatedSheet({ row, onClose }: { row: RegisterRow; onClose: () => void }) {
  const queryClient = useQueryClient()

  // Attendance work is open: the app's own announcements wait for the next load (D-731).
  useEffect(() => markAttendanceWorkOpened(), [])

  const { data: sheet, isPending } = useQuery({
    queryKey: ['sheet', row.occurrenceId],
    queryFn: () => fetchSheet(row.occurrenceId),
  })

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['sheet', row.occurrenceId] })
    queryClient.invalidateQueries({ queryKey: ['registers'] })
  }

  const mark = useMutation({
    mutationFn: ({ dancerId, state, note, familyNote }: { dancerId: string; state: AttendanceState; note?: string; familyNote?: string }) =>
      markAttendance(row.occurrenceId, [{ dancerId, state, note, familyNote }], true),
    onSettled: refresh,
  })

  return (
    <div className="page-shell px-5 py-8">
      <button
        type="button"
        onClick={onClose}
        className="text-[13.5px] font-semibold text-ink-3 hover:text-ink"
      >
        &larr; Back to attendance
      </button>

      {/*
        Read the name (D-581). The Jackrabbit one took four lines of the phone
        she is holding — "COMP_MT | TH 430 | 9/10 | MUSICAL THEATER | LARGE |
        NOVICE | VICTORIA" — above the roster she came for, on the screen a
        teacher opens more than any other. It keeps its `title`, because the
        full name is what she would quote to the office.
      */}
      <header className="mt-4 mb-4">
        <h1 className="font-display text-xl wrap-anywhere" title={row.className}>
          {readable(row.className)}
        </h1>
        <p className="mt-1 text-[13px] text-ink-3">
          {formatFullDate(row.date)}
          {row.roomName && <> &middot; {row.roomName}</>}
          {/* Here as well as on the row behind it (D-743): this is the screen
              she is on when she decides whether to go and ask somebody. */}
          {row.teachers.length > 0 && <> &middot; {row.teachers.map((t) => t.name).join(', ')}</>}
        </p>

        <p className="mt-2.5 rounded-xl border border-line bg-surface-2 px-4 py-2.5 text-[12.5px] text-ink-2">
          Filled in after the class, so these are recorded as entered later.
        </p>
      </header>

      {isPending && <p className="text-sm text-ink-3">Loading the roster...</p>}

      <div className="flex flex-col gap-2.5">
        {sheet?.enrolled.map((dancer) => (
          <div key={dancer.dancerId} className="rounded-2xl border border-line bg-surface px-4 py-3">
            <p className="text-[15px] font-semibold wrap-anywhere">{dancer.name}</p>

            <MarkButtons
              dancerName={dancer.name}
              state={dancer.state}
              note={dancer.note}
              familyNote={dancer.familyNote}
              columns={4}
              onMark={(state, note, familyNote) =>
                mark.mutate({ dancerId: dancer.dancerId, state, note, familyNote })
              }
            />
          </div>
        ))}
      </div>

      {sheet && sheet.enrolled.length === 0 && (
        <p className="rounded-2xl border border-line bg-surface px-5 py-8 text-center text-[14px] text-ink-3">
          Nobody was enrolled in this class on that date.
        </p>
      )}

      {/*
        <b>A dancer who turned up and is not on the roster</b> (D-632).

        The wall tablet has always had this; a teacher filling the register in
        from her own phone had not, and the pilot asked for both. Named
        against an absence it repairs that absence; named against nothing it
        banks for a class she misses later.
      */}
      {sheet && <AddToNight occurrenceId={row.occurrenceId} onAdded={() => void refresh()} />}
    </div>
  )
}


/**
 * Who is taking their registers, and who is not (D-743).
 *
 * `asked`, from QA: *"when a teacher does take attendance, there's no way for
 * tonya to view like attendance taken broken down by teacher, this would be
 * good so she can see easier who is and who isn't taking attendance."*
 *
 * <b>Both numbers, always.</b> The point of this view is the comparison - a
 * teacher with one gap out of forty and one with four out of four are the same
 * row on the night list, and opposite answers to the question she is asking.
 *
 * <b>Worst first, and everybody is listed</b>, including the teachers with
 * nothing outstanding: a name missing from a list reads as nothing to chase,
 * and it is also how somebody who has taken every register gets no credit for
 * it. Only the ones with a gap start open.
 */
function ByTeacher({ rows, onOpen }: { rows: RegisterRow[]; onOpen: (row: RegisterRow) => void }) {
  const groups = byTeacher(rows)

  if (groups.length === 0) return null

  return (
    <div className="flex flex-col gap-2">
      {groups.map((group) => {
        const outstanding = group.outstanding
        const total = group.taken + outstanding

        return (
          <details
            key={group.staffId ?? 'nobody'}
            open={outstanding > 0}
            className="group rounded-2xl border border-line bg-surface px-4 py-3"
          >
            <summary className="flex cursor-pointer list-none flex-wrap items-center gap-2 py-1">
              <span className="inline-block transition-transform group-open:rotate-90" aria-hidden="true">
                &#9656;
              </span>

              <span className="min-w-0 flex-1 text-[14px] font-semibold wrap-anywhere">
                {/*
                  Named for what it is rather than left blank: a private can
                  have no teacher on it (D-730), and a row with an empty name
                  reads as a bug rather than as a fact.
                */}
                {group.teacher ?? 'Nobody named'}
              </span>

              <span
                className={`shrink-0 rounded-md px-2 py-0.5 text-[12px] font-semibold tabular ${
                  outstanding > 0 ? 'bg-warn-bg text-warn' : 'bg-good-bg text-good'
                }`}
              >
                {outstanding > 0 ? (
                  <>
                    <AlertTriangle size={11} strokeWidth={2.5} className="mr-1 inline" />
                    {outstanding} still to mark
                  </>
                ) : (
                  <>
                    <Check size={11} strokeWidth={2.5} className="mr-1 inline" />
                    All {total} taken
                  </>
                )}
              </span>

              {outstanding > 0 && (
                <span className="tabular w-full text-[12.5px] text-ink-3">
                  {group.taken} of {total} taken
                </span>
              )}
            </summary>

            <div className="mt-2 flex flex-col gap-2">
              {group.rows.map((row) => (
                <RegisterButton key={row.occurrenceId} row={row} onOpen={onOpen} />
              ))}
            </div>
          </details>
        )
      })}
    </div>
  )
}

/**
 * One register on either list (D-743).
 *
 * <b>Shared rather than rebuilt</b>, because the night list and the teacher
 * list show the same thing and a second copy is where the two would drift -
 * the teacher was added to one of them and would have been missing from the
 * other on the day it was written.
 */
function RegisterButton({ row, onOpen }: { row: RegisterRow; onOpen: (row: RegisterRow) => void }) {
  return (
      <button
        type="button"
        onClick={() => onOpen(row)}
        className="flex flex-wrap items-center gap-3 rounded-2xl border border-line bg-surface px-4 py-3 text-left hover:border-ink-3"
      >
        <span className="min-w-0 flex-1">
          {/* Read the name here too (D-581), or the list she scans
              to find tonight's class is a wall of Jackrabbit. */}
          <span
            className="block text-[14px] font-semibold wrap-anywhere"
            title={row.className}
          >
            {readable(row.className)}
          </span>
          <span className="mt-0.5 block text-[12.5px] text-ink-3">
            {row.startTime && (
              <span className="tabular">
                {clockTime(row.startTime)}
                {meridiem(row.startTime)}
              </span>
            )}
            {row.roomName && <> &middot; {row.roomName}</>}
            {/*
              <b>Whose register it is</b> (D-743). `asked`, from QA:
              the row named the class and the room, and the owner
              opening this screen is chasing a *person* - so the one
              fact she needs to act on was the one it left out.
              Nobody named is left silent rather than filled with a
              guess: a private can genuinely have no teacher (D-730).
            */}
            {row.teachers.length > 0 && <> &middot; {row.teachers.map((t) => t.name).join(', ')}</>}
          </span>
        </span>
  
        <span
          className={`shrink-0 rounded-md px-2 py-0.5 text-[12px] font-semibold tabular ${
            row.isIncomplete ? 'bg-warn-bg text-warn' : 'bg-good-bg text-good'
          }`}
        >
          {row.isIncomplete && (
            <AlertTriangle size={11} strokeWidth={2.5} className="mr-1 inline" />
          )}
          {row.marked} of {row.expected}
        </span>
      </button>
  )
}
